import type { ContactDetailsBlockProps, SiteProfileData } from '../types';
import { theme, sectionColors, cardTextColor, PAGE_TEXT_COLOR } from '../theme';
import { BuildingIcon, MailIcon, PhoneIcon, SocialIcon } from './contactIcons';

const NETWORKS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
] as const;

/**
 * How to reach the site: phone, email and brokerage, office hours, and social links.
 *
 * Phone, email, brokerage and the social links are LIVE — read from the host's site config
 * (`BlockData.profile`) at render time, never stored in the block. Only the headings and the
 * office hours are the block's own content, because they exist nowhere else. This is the
 * "Get in Touch" column the built-in Contact page has always rendered.
 */
export function ContactDetailsBlock({
  props,
  profile,
  companyName,
}: {
  props: ContactDetailsBlockProps;
  profile?: SiteProfileData | null;
  companyName?: string | null;
}) {
  const { heading, hoursHeading, hours = [], showSocial = true, socialHeading } = props;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const rows = (Array.isArray(hours) ? hours : []).filter((r) => r && (r.days?.trim() || r.hours?.trim()));
  const social = profile?.social ?? null;
  const links = showSocial && social ? NETWORKS.filter((n) => typeof social[n.key] === 'string' && social[n.key]!.trim()) : [];
  const items = [
    profile?.phone ? { key: 'phone', label: 'Phone', icon: PhoneIcon, value: profile.phone, href: `tel:${profile.phone}` } : null,
    profile?.email ? { key: 'email', label: 'Email', icon: MailIcon, value: profile.email, href: `mailto:${profile.email}` } : null,
    profile?.brokerage_name ? { key: 'brokerage', label: 'Brokerage', icon: BuildingIcon, value: profile.brokerage_name, href: null } : null,
  ].filter((x): x is NonNullable<typeof x> => x !== null);

  const card = 'blk-card rounded-2xl border border-black/10 p-8 shadow-sm';
  // The cards are the page background; their words stay readable on it, whatever the section's text.
  const cardStyle = { backgroundColor: theme.background, color: cardTextColor(props, { surfaceInk: PAGE_TEXT_COLOR }) };
  const iconWrap = { backgroundColor: `color-mix(in srgb, ${theme.primary} 9%, transparent)`, color: theme.primary };
  const follow = socialHeading?.trim() || `Follow ${companyName?.trim() || 'Us'}`;

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-2">
        <div className={card} style={cardStyle}>
          {heading ? (
            <h2 className="mb-6 text-2xl font-bold" style={{ fontFamily: theme.headingFont }}>
              {heading}
            </h2>
          ) : null}
          <div className="flex flex-col gap-5">
            {items.map(({ key, label, icon: Icon, value, href }) => (
              <div key={key} className="flex items-start gap-4">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={iconWrap}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide opacity-50">{label}</p>
                  {href ? (
                    <a href={href} className="break-all font-medium hover:underline">
                      {value}
                    </a>
                  ) : (
                    <p className="font-medium">{value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-6">
          {rows.length > 0 ? (
            <div className={card} style={cardStyle}>
              {hoursHeading ? (
                <h3 className="mb-4 text-lg font-bold" style={{ fontFamily: theme.headingFont }}>
                  {hoursHeading}
                </h3>
              ) : null}
              <div className="flex flex-col gap-2 text-sm">
                {rows.map((r, i) => (
                  <div key={`${i}-${r.days}`} className="flex justify-between gap-4">
                    <span className="opacity-60">{r.days}</span>
                    <span className="font-medium">{r.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {links.length > 0 ? (
            <div className={card} style={cardStyle}>
              <h3 className="mb-4 text-lg font-bold" style={{ fontFamily: theme.headingFont }}>
                {follow}
              </h3>
              <div className="flex flex-wrap gap-3">
                {links.map((n) => (
                  <a
                    key={n.key}
                    href={social![n.key]!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-black/15 px-4 py-2 text-sm font-medium hover:opacity-80"
                  >
                    <SocialIcon network={n.key} className="h-4 w-4" />
                    {n.label}
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
